import React from 'react';

import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';

import AiReviewFindings, { FindingChip, IAiReviewFinding, reasonLines } from './AiReviewFindings';
import { renderReasonWithSourceChips } from './aiReviewReason';

const useStyles = makeStyles(theme => ({
  // One line per point of the reason, its finding chip first.
  line: {
    display: 'flex',
    gap: theme.spacing(0.75),
    '& + &': {
      marginTop: theme.spacing(0.5),
    },
  },
  bullet: {
    flexShrink: 0,
  },
  lineText: {
    flex: 1,
    minWidth: 0,
  },
  lineChip: {
    marginRight: theme.spacing(0.75),
    verticalAlign: 'middle',
  },
  findings: {
    marginTop: theme.spacing(0.5),
  },
  // A line drawn through the text, line by line, when the field changed since the review.
  '@keyframes aiVerdictStrike': {
    from: { backgroundSize: '0% 1px' },
    to: { backgroundSize: '100% 1px' },
  },
  struck: {
    backgroundImage: 'linear-gradient(currentColor, currentColor)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '0 55%',
    backgroundSize: '100% 1px',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    animation: '$aiVerdictStrike 0.6s ease-out',
    cursor: 'help',
  },
  sourceChip: {
    maxWidth: 'calc(100% - 8px)',
    height: 20,
    margin: theme.spacing(0, 0.5),
    verticalAlign: 'middle',
    textDecoration: 'none',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

interface IAiReviewReasonPointsProps {
  reason?: string | null;
  findings?: IAiReviewFinding[] | null;
  // Shown when there is no reason.
  empty?: React.ReactNode;
  // Set when the field changed since the review: the text is struck through, with this as its tooltip.
  struckHint?: string;
}

// The reason of a field verdict, a point per line with the finding of the point first. The prompt asks
// for one finding per point, in order; when they do not match, the chips go together under the reason.
const AiReviewReasonPoints = ({ reason, findings, empty = null, struckHint }: IAiReviewReasonPointsProps) => {
  const classes = useStyles();
  const lines = reasonLines(reason);
  const list = findings || [];
  const perPoint = !!list.length && lines.filter(line => line.point).length === list.length;
  const findingOfPoint: Record<number, IAiReviewFinding> = {};
  if (perPoint) {
    let next = 0;
    lines.forEach((line, index) => {
      if (line.point) findingOfPoint[index] = list[next++];
    });
  }
  return (
    <>
      {!lines.length && empty}
      {lines.map((line, index) => (
        <div key={index} className={classes.line} data-testid="ai-field-verdict-line">
          <span className={classes.bullet}>{line.point ? '•' : ''}</span>
          <span className={classes.lineText}>
            {line.point && findingOfPoint[index] && (
              <span className={classes.lineChip}>
                <FindingChip finding={findingOfPoint[index]} />
              </span>
            )}
            {struckHint ? (
              <Tooltip arrow enterTouchDelay={0} title={struckHint}>
                <span className={classes.struck} data-testid="ai-field-verdict-struck">
                  {renderReasonWithSourceChips(line.text, classes.sourceChip)}
                </span>
              </Tooltip>
            ) : (
              renderReasonWithSourceChips(line.text, classes.sourceChip)
            )}
          </span>
        </div>
      ))}
      {!perPoint && !!list.length && (
        <div className={classes.findings}>
          <AiReviewFindings findings={list} reason={reason} />
        </div>
      )}
    </>
  );
};

export default AiReviewReasonPoints;
